import { cfetch } from '@confluence/network';
import { throwErrorWithResponse } from '@confluence/graphql/entry-points/throwErrorWithResponse';
import type { BadStatusError } from '@confluence/network';

export type FetchOptions<TVariables, TResponse> = {
	url: string | ((variables: TVariables) => string);
	method?: string;
	headers?: HeadersInit;
	body?: string | ((variables: TVariables) => string);
	credentials?: RequestCredentials;
	parseResponseOnSuccess?: (response: Response) => TResponse | Promise<TResponse>;
	parseResponseOnError?: (
		error: Error | BadStatusError,
		variables: TVariables,
	) => TResponse | Promise<TResponse>;
};

export async function fetchResponse<TVariables, TResponse>(
	fetchOptions: FetchOptions<TVariables, TResponse>,
	variables: TVariables,
): Promise<TResponse> {
	const { url, method, body, headers, credentials, parseResponseOnSuccess, parseResponseOnError } =
		fetchOptions;
	try {
		const defaultBody = method === 'GET' ? undefined : JSON.stringify(variables);

		const response = await cfetch(typeof url === 'function' ? url(variables) : url, {
			method,
			body: typeof body === 'function' ? body(variables) : body || defaultBody,
			headers,
			credentials,
		});

		if (parseResponseOnSuccess) {
			return parseResponseOnSuccess(response);
		}

		return response.status === 204
			? ({} as TResponse)
			: await response.json().catch((error) => {
					throw throwErrorWithResponse(error, response);
				});
	} catch (error) {
		if (parseResponseOnError) {
			return parseResponseOnError(error, variables);
		}
		throw error;
	}
}
